import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { DependencyFilterKeys } from '../../../../shared/dependenciesV2';
import { MANUAL_DEPENDENCIES_REVAMP } from '../../../../shared/featureFlags/flagNames';
import { useDresser } from '../../../contexts/DrawerContext';
import { useIsFeatureEnabled } from '../../../helpers/features';
import * as DependenciesV2Store from '../../../redux/dependenciesV2';
import AddDependencyDrawer from '../../drawers/AddDependencyDrawer';
import { SearchInput } from '../../generic';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button';

import useFetchDependencyAction from './hooks/useFetchDependencyAction';
import type { DependencyV2Modals } from './hooks/useDependencyModals';

import styles from './dependencies.module.less';

type Props = {
  depModals: DependencyV2Modals;
};

const DependenciesHeader: VoidFunctionComponent<Props> = ({ depModals: { addDependencyModal } }) => {
  const setSearchFilterAndFetch = useFetchDependencyAction(DependenciesV2Store.actions.setFilters);
  const filters = useSelector(DependenciesV2Store.selectors.getFilters);
  const isProjectScope = useSelector(DependenciesV2Store.selectors.getIsProjectScope);
  const isOrgMember = useSelector(DependenciesV2Store.selectors.getIsOrgMember);
  const scope = useSelector(DependenciesV2Store.selectors.getScope);

  const { pushDrawer } = useDresser();
  const isManualDependenciesRevampEnabled = useIsFeatureEnabled(MANUAL_DEPENDENCIES_REVAMP);

  const handleSearchInput = (value: string) => setSearchFilterAndFetch({
    ...filters,
    [DependencyFilterKeys.Title]: value,
  });

  const handleAddDependency = () => {
    if (isManualDependenciesRevampEnabled && scope?.scope === 'project') {
      pushDrawer(<AddDependencyDrawer userRevisionLocator={scope.revision} />);
    } else {
      addDependencyModal.onOpen({ context: { mode: 'new' } });
    }
  };

  return (
    <div className={styles.DependenciesHeader}>
      <div className={styles.DependenciesHeaderSearch}>
        <SearchInput
          label="dependency-name"
          placeholder="Filter by name or locator"
          size="medium"
          value={filters.title}
          onChange={handleSearchInput}
        />
      </div>
      {isOrgMember && isProjectScope && (
        <Button type={BUTTON_TYPE.SECONDARY} size={BUTTON_SIZE.LARGE} onClick={handleAddDependency}>
          Add Dependency
        </Button>
      )}
    </div>
  );
};

export default DependenciesHeader;
